// Splits an audio file (mp3, m4a, wav, webm … anything the browser can decode)
// into short mono 16 kHz WAV pieces, because the speech service accepts about
// 30 seconds of audio per request. Used for uploaded consultation recordings
// (G4). Decoding happens in the browser, so the server needs no audio tools.

export interface AudioPiece { blob: Blob; start: number; end: number }

const TARGET_RATE = 16000;

function encodeWav(samples: Float32Array, sampleRate: number): Blob {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const write = (offset: number, text: string) => { for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i)); };
  write(0, 'RIFF'); view.setUint32(4, 36 + samples.length * 2, true); write(8, 'WAVE');
  write(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true); view.setUint32(28, sampleRate * 2, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true);
  write(36, 'data'); view.setUint32(40, samples.length * 2, true);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i] ?? 0));
    view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return new Blob([buffer], { type: 'audio/wav' });
}

/** Decodes `file` and returns pieces of at most `pieceSeconds`, plus the total duration. */
export async function splitAudioFile(file: Blob, pieceSeconds = 25): Promise<{ pieces: AudioPiece[]; duration: number }> {
  const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const ctx = new Ctx();
  let decoded: AudioBuffer;
  try {
    decoded = await ctx.decodeAudioData(await file.arrayBuffer());
  } catch {
    throw new Error('This audio file could not be read. Use an mp3, m4a, wav or webm recording.');
  } finally {
    void ctx.close();
  }
  // Mix down to mono and resample to 16 kHz in one offline render.
  const length = Math.max(1, Math.ceil(decoded.duration * TARGET_RATE));
  const offline = new OfflineAudioContext(1, length, TARGET_RATE);
  const src = offline.createBufferSource();
  src.buffer = decoded;
  src.connect(offline.destination);
  src.start();
  const mono = (await offline.startRendering()).getChannelData(0);

  const per = pieceSeconds * TARGET_RATE;
  const pieces: AudioPiece[] = [];
  for (let at = 0; at < mono.length; at += per) {
    const slice = mono.subarray(at, Math.min(mono.length, at + per));
    pieces.push({ blob: encodeWav(slice, TARGET_RATE), start: at / TARGET_RATE, end: (at + slice.length) / TARGET_RATE });
  }
  return { pieces, duration: decoded.duration };
}
